import FilterElementable from "../../contentManagement/filterElementable";
import TrackedModel from "../../tracked/trackedModel";
import {ISortableFilterElement} from "../../../interfaces/contentManagementInterfaces";
import Guid from "../../../helpers/guid";
import IRepo from "../../../interfaces/repo";
import Provider from "../../../providers/new/provider";
import Model from "../../model";
import ElementHandler from "../../../services/elementHandler";
import {RelationType} from "../../relationType";
import {ISelectedProperty} from "../../../state/modules/singleSelectedProperty";
import Repo from "../../../state/modules/repo";
import Selected from "../../../../formability/listViewBuilder/store/modules/selected";
import ListViewFilterElementCapabilityManager from "./listViewFilterElementCapabilityManager";
import ListViewFilterElementDefinition from "./listViewFilterElementDefinition";
import {ListViewFilterElementCapability} from "./listViewFilterElementCapability";
import {ListViewFilterElementType} from "../../../enums/listViews/listViewFilterElementType";
import ObjectModelProperty from "../../objectModelProperties/objectModelProperty";
import ListView from "../listView";
import {Hash} from "../../../interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "../../objectModels/objectModelConditionalRuleSet";
import IConditionalRuleContainer from "../../conditionalRules/conditionalRuleContainer";
import {ILabelable} from "../../../interfaces/propertyInterfaces";
import ObjectModelConditionalFields, {ListViewFilterElementConditionalField} from "../../objectModels/objectModelConditionalFields";
import ConditionalRuleSetFactory from "../../conditionalRules/conditionalRuleSetFactory";
import {ObjectHelper} from "../../../helpers/objectHelper";
import {ListViewFilterLayoutElementType} from "../../../enums/listViews/listViewFilterLayoutElementType";
import assertNever from "../../../../global/assetNever";
import {ListViewFilterControlElementType} from "../../../enums/listViews/listViewFilterControlElementType";
import {SearchMatchType} from "~/enums/searchMatchType";
import IProvider from "../../../interfaces/provider";
import ObjectModelDefaultPropertyReference from "~/models/objectModels/objectModelDefaultPropertyReference";
import Vue from "vue";
import {ObjectModelBuiltinPropertyType} from "~/enums/objectModelBuiltinPropertyType";
import {DatetimeMode} from "~/models/datetimeMode";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";

export default class ListViewFilterElement extends FilterElementable(TrackedModel) implements ISortableFilterElement, IConditionalRuleContainer, ILabelable {
  _classKey = "filterElement";
  rootModel: ListView;
  filter_type: number;
  sort_order: number;
  label_title: string;
  placeholder?: string;
  property_to_filter_id?: string;
  property_to_filter_is_default?: boolean;
  property_to_filter_default_type?: ObjectModelBuiltinPropertyType;
  default_property_filter_option_id?: number;
  default_property_filter_option_ids?: Array<string>; // for use with multiselectable property to filter
  properties_to_search_ids?: Array<string>;
  default_properties_to_search?:  { [key: string]: ObjectModelDefaultPropertyReference} = {} ;
  start_datetime?: string;
  end_datetime?: string;
  item_inclusion_condition?: boolean;
  item_inclusion_condition_conditional_rule_sets?: Hash<ObjectModelConditionalRuleSet>;
  match_type: SearchMatchType;
  add_ssn_dashes: boolean;

  except = ['filterElement', 'createEmbeddedObject', 'organization', 'saveTimer', '_organization'];

  get TRACKED_ARRAYS(): string[] {
    return ['properties_to_search_ids']
  }

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
    this.defineProperty('add_ssn_dashes', false);

    for (const definition of ObjectModelConditionalFields.LIST_VIEW_FILTER_ELEMENT_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  _filterElementHandler: ElementHandler = new ElementHandler(this, ListViewFilterElement, 'filter_elements');

  static repo: IRepo<ListViewFilterElement> = new Repo<ListViewFilterElement>('listViewFilterElements', ListViewFilterElement, {}, new Provider(ListViewFilterElement, 'filter_element', 'filter_elements'));
  repo: IRepo<this, IProvider<this>>;


  static get provider(): IProvider<Model> {
    return this.repo.provider;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  get elementHandler(): ElementHandler {
    return this.filterElementHandler;
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      filter_elements: {type: typeof this, relationType: RelationType.dictionary},
      default_properties_to_search: {type:ObjectModelDefaultPropertyReference , relationType: RelationType.dictionary}
    };
    for (const definition of ObjectModelConditionalFields.LIST_VIEW_FILTER_ELEMENT_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  createEmbeddedObject = {
    filter_elements: (parent: object, json: object): ListViewFilterElement => new ListViewFilterElement(),
    default_properties_to_search: (parent: object, json: object): ObjectModelDefaultPropertyReference => new ObjectModelDefaultPropertyReference(this,'default_properties_to_search' ,json),
  };

  initialize(root: ListView, parent = root, type, json= {}) {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty('filter_type', type);
    this.defineProperty('sort_order', 0);
    this.addDefaultProperties();
    this.loadFromJson(json);

    for (const definition of ObjectModelConditionalFields.LIST_VIEW_FILTER_ELEMENT_FIELDS) {
      if (definition.defaultConditionalValue instanceof Function) {
        this[definition.fieldName] = definition.defaultConditionalValue(this);
      } else {
        this.defineProperty(definition.fieldName, definition.defaultConditionalValue);
      }
    }

    return this;
  }

  appendDefaultPropertyToSearch(json = {}){
    let propertyReference = this.createEmbeddedObject.default_properties_to_search(this, json );
    this.trackCreationDeletion(propertyReference.client_id, null, propertyReference.asJson(), propertyReference);
    Vue.set(this[propertyReference.rootName], propertyReference.client_id, propertyReference);
  }
  get defaultPropertiesToSearchArray(): Array<ObjectModelDefaultPropertyReference>{
    return  ObjectHelper.toArray(this.default_properties_to_search)
  }

  addDefaultProperties(): void {
    ListViewFilterElementCapabilityManager.addDefaultProperties(this);
  }

  static get rootName(): string {
    return 'filter_elements';
  }

  get rootName(): string {
    return ListViewFilterElement.rootName;
  }

  addToRepo(): void {
    ListViewFilterElement.repo.add(this)
  }

  get listView(): ListView {
    return this.rootModel;
  }

  get selector(): ISelectedProperty {
    return Selected.listViewFilterElement;
  }

  select(): void {
    this.selector.set(this);
  }

  deselect(): void {
    this.selector.deselectIf(this);
  }

  get isSelectable(): boolean {
    return this.can(ListViewFilterElementCapability.selectable);
  }

  get isSelected(): boolean {
    return this.selector.is(this);
  }

  get icon(): string {
    return ListViewFilterElementDefinition.getIcon(this.filter_type);
  }

  get displayName(): string {
    return ListViewFilterElementDefinition.getDisplayName(this.filter_type);
  }

  can(duty: ListViewFilterElementCapability): boolean {
    return ListViewFilterElementCapabilityManager.can(this.filter_type, duty);
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.filterElementHandler.recalculateElementPositions();
  }

  get isLast(): boolean {
    return this.sort_order === this.parent.filterElementHandler.lastSortOrder();
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  is(...filterTypes: Array<ListViewFilterElementType>): boolean {
    return filterTypes.includes(this.filter_type);
  }

  isNot(...filterTypes: Array<ListViewFilterElementType>): boolean {
    return !this.is(...filterTypes);
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.filterElementHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.filterElementHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ListViewFilterElementConditionalField): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    if (this.rootModel.hasOwnProperty('object_model')) {
      return this.rootModel.object_model.objectModelPropertiesArray.filter(property => property.objectModel.isPropertyIndexed(property));
    }
  
    return this.rootModel.published_object_model.objectModelPropertiesArray.filter(property => property.objectModel.isPropertyIndexed(property));
  }

  get propertyToFilter():
    ObjectModelProperty |
    ObjectModelRelation |
    { datetime_mode: DatetimeMode, property_to_filter_default_type?: ObjectModelBuiltinPropertyType } {
    if (this.property_to_filter_is_default) {
      return {datetime_mode: DatetimeMode.Datetime, property_to_filter_default_type: this.property_to_filter_default_type}
    } else {
      if (this.rootModel.hasOwnProperty('object_model')) {
        return this.rootModel.object_model.findObjectModelProperty(this.property_to_filter_id) ||
          this.rootModel.object_model.findObjectModelRelation(this.property_to_filter_id);
      }

      return this.rootModel.published_object_model.findObjectModelProperty(this.property_to_filter_id) ||
        // @ts-ignore // there is no front end PublishedObjectModelRelation model, so .object_model_relation_id isn't found.
        this.rootModel.published_object_model.objectModelRelationsArray.find(relation => relation.object_model_relation_id === this.property_to_filter_id);
    }
  }

  get isExpandable() {
    return this.is(ListViewFilterLayoutElementType.Grid);
  }

  get isConfigured(): boolean {
    const type: ListViewFilterElementType = this.filter_type;
    switch(type) {
      case ListViewFilterControlElementType.Dropdown:
        return !!this.property_to_filter_id;
      case ListViewFilterControlElementType.SearchBar:
        const ids =  this.properties_to_search_ids;
        const defaultValues = this.defaultPropertiesToSearchArray.map(x => x.type)
        const all = [...ids, ...defaultValues]
        return Array.isArray(all) &&  all.length > 0;
      case ListViewFilterControlElementType.Toggle:
        const sets = this.item_inclusion_condition_conditional_rule_sets;
        return sets &&  Object.keys(sets).length > 0;
      case ListViewFilterLayoutElementType.Grid:
        return true;
      case ListViewFilterLayoutElementType.Stack:
        return true;
      case ListViewFilterControlElementType.DateRange:
        if(this.property_to_filter_is_default){
          return !!this.property_to_filter_default_type
        }else{
          return !!this.property_to_filter_id;
        }
      default:
        assertNever(type)
    }
  }
}

